import React, { MouseEvent, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { ArrowLink, AsyncDataPlaceholder, ITheme } from '../../../../../ui';
import { useTranslation } from '../../../../shared/i18n/translate/useTranslation';
import { CancelBookingDialog } from './CancelBookingDialog';
import { useCancelBooking } from './helpers/useCancelBooking';
import { ErrorBar } from '../../../../shared/components';
import { trackClickedCancelBookingAbort } from '../../booking-steps/shared/analytics/custom/trackClickedCancelBookingAbort';
import { trackClickedCancelBookingConfirm } from '../../booking-steps/shared/analytics/custom/trackClickedCancelBookingConfirm';
import { IBooking } from '../../../domain/booking/models/IBooking';
import { getIsCancelAllowed } from '../../../domain/booking/stores/utils/getIsCancelAllowed';
import { useNavigate } from '../../../../shared/router/useNavigate';
import themed from '../../../../../ui/themer/themed';

interface ICancelBookingLinkProps {
	booking: IBooking;
	nextUrl: string;
}

export const CancelBooking = ({
	booking,
	nextUrl,
}: ICancelBookingLinkProps) => {
	const { t } = useTranslation();
	const [isDialogOpen, setIsDialogOpen] = useState(false);
	const [isCancelBookingUnavailable, setIsCancelBookingUnavailable] =
		useState(false);
	const { cancelBooking, isLoading, error, isCancelled } =
		useCancelBooking(booking);
	const navigate = useNavigate();

	const onClickLink = (e: MouseEvent) => {
		e.preventDefault();

		if (!getIsCancelAllowed(booking)) {
			setIsCancelBookingUnavailable(true);
			return;
		}

		setIsDialogOpen(true);
	};

	const onConfirm = () => {
		if (isLoading) return;

		trackClickedCancelBookingConfirm();

		cancelBooking();
	};

	const onAbort = () => {
		if (isLoading) return;

		trackClickedCancelBookingAbort();

		setIsDialogOpen(false);
	};

	useEffect(() => {
		if (isCancelled) {
			setIsDialogOpen(false);
			navigate(nextUrl);
		}
	}, [isCancelled]);

	return (
		<>
			<StyledCancelLink href={nextUrl} onClick={onClickLink}>
				{t('parking_booking_details_cancel_booking_link')}
			</StyledCancelLink>
			{isCancelBookingUnavailable && (
				<ErrorBar>
					{t('web_parking_booking_details_cancel_amend_unavailable_error')}
				</ErrorBar>
			)}
			{isDialogOpen && (
				<CancelBookingDialog
					onConfirm={onConfirm}
					onAbort={onAbort}
					disabled={isLoading}
					error={error}
				/>
			)}
			{isLoading && <AsyncDataPlaceholder />}
		</>
	);
};

const StyledCancelLink = styled(ArrowLink)<{}, ITheme>`
	&,
	&:hover {
		color: ${themed(({ color }) => color.state.error)};

		svg g {
			stroke: ${themed(({ color }) => color.state.error)};
		}
	}

	&:hover {
		&:after {
			background: ${themed(({ color }) => color.state.error)};
		}
	}
`;
